/**
 * Réglages. (Brief §3.6)
 */

import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { Settings } from "@rm/shared";

import { api } from "../lib/api.js";
import * as push from "../lib/push.js";
import { Badge, Button, Card, Field, Input, Spinner, Textarea } from "../components/ui.js";

interface Account {
  id: number;
  handle: string;
  display_name: string | null;
  tier: string;
  x_user_id: string | null;
  swipes_right: number;
  swipes_left: number;
  learned_weight: number;
  followers_count: number | null;
}

interface SettingsResponse {
  settings: Settings;
  integrations: {
    x_configured: boolean;
    x_connected: boolean;
    anthropic: boolean;
    push: boolean;
  };
}

export function SettingsPage({ onLogout }: { onLogout: () => void }) {
  const [data, setData] = useState<SettingsResponse | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    const [settings, list] = await Promise.all([
      api.get<SettingsResponse>("/settings"),
      api.get<{ accounts: Account[] }>("/settings/accounts"),
    ]);
    setData(settings);
    setAccounts(list.accounts);
  }, []);

  useEffect(() => {
    load().catch(() => setMessage("Chargement impossible"));
  }, [load]);

  // Retour du callback OAuth X.
  useEffect(() => {
    const status = params.get("x");
    if (!status) return;
    setMessage(
      status === "ok"
        ? "Compte X connecté."
        : status === "refuse"
          ? "Connexion X refusée."
          : "Connexion X en échec — voir les logs serveur.",
    );
    params.delete("x");
    setParams(params, { replace: true });
  }, [params, setParams]);

  async function patch(partial: Record<string, unknown>) {
    setSaving(true);
    try {
      const response = await api.put<{ settings: Settings }>("/settings", partial);
      setData((current) => (current ? { ...current, settings: response.settings } : current));
      setMessage("Enregistré.");
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Enregistrement impossible");
    } finally {
      setSaving(false);
    }
  }

  if (!data) return <Spinner />;
  const { settings, integrations } = data;

  return (
    <div className="flex flex-col gap-4 px-4 pt-4">
      {message && (
        <p className="rounded-lg bg-[var(--color-surface-2)] px-3 py-2 text-xs">{message}</p>
      )}

      <Integrations integrations={integrations} onChange={load} />

      <Card>
        <h2 className="mb-3 text-sm font-medium">Deck</h2>
        <div className="flex flex-col gap-4">
          <Field
            label={`Curseur découverte — ${settings.deck.discovery_ratio} %`}
            hint={`${Math.round((settings.deck.cards_per_batch * (100 - settings.deck.discovery_ratio)) / 100)} core / ${Math.round((settings.deck.cards_per_batch * settings.deck.discovery_ratio) / 100)} découverte par pull`}
          >
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={settings.deck.discovery_ratio}
              onChange={(e) => patch({ deck: { discovery_ratio: Number(e.target.value) } })}
              className="accent-[var(--color-accent)]"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Cartes par pull">
              <Input
                type="number"
                min={1}
                max={50}
                defaultValue={settings.deck.cards_per_batch}
                onBlur={(e) => patch({ deck: { cards_per_batch: Number(e.target.value) } })}
              />
            </Field>
            <Field label="Objectif / jour">
              <Input
                type="number"
                min={1}
                max={50}
                defaultValue={settings.deck.daily_goal}
                onBlur={(e) => patch({ deck: { daily_goal: Number(e.target.value) } })}
              />
            </Field>
          </div>
        </div>
      </Card>

      <Card>
        <h2 className="mb-3 text-sm font-medium">Budget</h2>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Plafond / jour (€)">
            <Input
              type="number"
              step="0.1"
              min={0}
              defaultValue={settings.budget.daily_cap_eur}
              onBlur={(e) => patch({ budget: { daily_cap_eur: Number(e.target.value) } })}
            />
          </Field>
          <Field label="Taux EUR→USD">
            <Input
              type="number"
              step="0.01"
              min={0}
              defaultValue={settings.budget.eur_usd_rate}
              onBlur={(e) => patch({ budget: { eur_usd_rate: Number(e.target.value) } })}
            />
          </Field>
        </div>

        <p className="mt-3 mb-2 text-xs text-[var(--color-muted)]">
          Coûts unitaires X en USD. X reprice sans préavis — ces champs existent pour pouvoir
          corriger sans redéployer.
        </p>
        <div className="grid grid-cols-3 gap-2">
          {(["post", "user", "owned"] as const).map((key) => (
            <Field key={key} label={key}>
              <Input
                type="number"
                step="0.001"
                min={0}
                defaultValue={settings.budget.unit_costs_usd[key]}
                onBlur={(e) =>
                  patch({ budget: { unit_costs_usd: { [key]: Number(e.target.value) } } })
                }
              />
            </Field>
          ))}
        </div>
      </Card>

      <Card>
        <h2 className="mb-3 text-sm font-medium">Ton et territoires</h2>
        <div className="flex flex-col gap-3">
          <Field label="Mots-clés découverte" hint="Séparés par des virgules.">
            <Input
              defaultValue={settings.keywords.join(", ")}
              onBlur={(e) =>
                patch({ keywords: splitList(e.target.value) })
              }
            />
          </Field>
          <Field label="Territoires" hint="Utilisés par la classification Haiku.">
            <Input
              defaultValue={settings.territories.join(", ")}
              onBlur={(e) => patch({ territories: splitList(e.target.value) })}
            />
          </Field>
          <Field label="Notes de ton">
            <Textarea
              defaultValue={settings.tone_notes}
              onBlur={(e) => patch({ tone_notes: e.target.value })}
            />
          </Field>
          <FewShots settings={settings} onSave={patch} />
          <Assets settings={settings} onSave={patch} />
        </div>
      </Card>

      <Notifications settings={settings} onSave={patch} />

      <CoreList accounts={accounts} onChange={load} />

      <Jobs />

      <Card>
        <div className="flex items-center justify-between">
          <span className="text-sm">Session</span>
          <Button
            variant="danger"
            onClick={async () => {
              await api.post("/auth/logout");
              onLogout();
            }}
          >
            Se déconnecter
          </Button>
        </div>
      </Card>

      {saving && <p className="text-center text-xs text-[var(--color-muted)]">Enregistrement…</p>}
      {/* Quelle version je regarde ? — tranche les doutes PWA/service worker. */}
      <p className="pb-6 pt-2 text-center text-[10px] text-[var(--color-muted)] opacity-60">
        build {__BUILD_AT__}
      </p>

    </div>
  );
}

function splitList(value: string): string[] {
  return value
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);
}

function Integrations({
  integrations,
  onChange,
}: {
  integrations: SettingsResponse["integrations"];
  onChange: () => void;
}) {
  return (
    <Card>
      <h2 className="mb-3 text-sm font-medium">Intégrations</h2>
      <ul className="flex flex-col gap-2 text-sm">
        <li className="flex items-center justify-between">
          <span>API X</span>
          {!integrations.x_configured ? (
            <Badge tone="bad">clés serveur absentes</Badge>
          ) : integrations.x_connected ? (
            <div className="flex items-center gap-2">
              <Badge tone="good">connecté</Badge>
              <Button
                variant="ghost"
                onClick={async () => {
                  await api.post("/x/disconnect");
                  onChange();
                }}
              >
                Déconnecter
              </Button>
            </div>
          ) : (
            <a
              href="/api/x/connect"
              className="rounded-lg bg-[var(--color-accent)] px-3 py-1.5 text-xs font-medium text-white"
            >
              Connecter X
            </a>
          )}
        </li>
        <li className="flex items-center justify-between">
          <span>Anthropic (angles)</span>
          <Badge tone={integrations.anthropic ? "good" : "bad"}>
            {integrations.anthropic ? "configuré" : "clé absente"}
          </Badge>
        </li>
        <li className="flex items-center justify-between">
          <span>Web Push</span>
          <Badge tone={integrations.push ? "good" : "warn"}>
            {integrations.push ? "configuré" : "VAPID absent"}
          </Badge>
        </li>
      </ul>
      <p className="mt-3 text-[11px] text-[var(--color-muted)]">
        Scopes X demandés : <code>tweet.read users.read offline.access</code>. Aucun scope
        d'écriture — l'app ne peut pas publier.
      </p>
    </Card>
  );
}

function Notifications({
  settings,
  onSave,
}: {
  settings: Settings;
  onSave: (p: Record<string, unknown>) => Promise<void>;
}) {
  const [state, setState] = useState(push.permission());
  const [error, setError] = useState<string | null>(null);
  const needsInstall = push.needsInstall();

  return (
    <Card>
      <h2 className="mb-3 text-sm font-medium">Notifications</h2>

      {needsInstall && (
        <p className="mb-3 rounded-lg bg-[#2a2410] px-3 py-2 text-xs text-[var(--color-warn)]">
          Sur iPhone, les notifications ne fonctionnent que si l'app est installée sur l'écran
          d'accueil : Partager → « Sur l'écran d'accueil ».
        </p>
      )}

      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <span className="text-sm">Autorisation</span>
          {state === "granted" ? (
            <Badge tone="good">accordée</Badge>
          ) : (
            <Button
              onClick={async () => {
                const err = await push.subscribe();
                setError(err);
                setState(push.permission());
              }}
              disabled={needsInstall}
            >
              Activer
            </Button>
          )}
        </div>

        {error && <p className="text-xs text-[var(--color-bad)]">{error}</p>}

        <Field label="Heures de rappel" hint="Format HH:MM, séparées par des virgules.">
          <Input
            defaultValue={settings.notifications.reminders.join(", ")}
            onBlur={(e) => onSave({ notifications: { reminders: splitList(e.target.value) } })}
          />
        </Field>

        <label className="flex items-center justify-between text-sm">
          <span>Alerte opportunité chaude</span>
          <input
            type="checkbox"
            defaultChecked={settings.notifications.hot_opportunity}
            onChange={(e) => onSave({ notifications: { hot_opportunity: e.target.checked } })}
            className="accent-[var(--color-accent)]"
          />
        </label>

        <Field label={`Seuil « hot » — ${settings.notifications.hot_threshold}`}>
          <input
            type="range"
            min={50}
            max={100}
            defaultValue={settings.notifications.hot_threshold}
            onMouseUp={(e) =>
              onSave({ notifications: { hot_threshold: Number((e.target as HTMLInputElement).value) } })
            }
            className="accent-[var(--color-accent)]"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Silence — début">
            <Input
              defaultValue={settings.notifications.quiet_hours.start}
              onBlur={(e) =>
                onSave({ notifications: { quiet_hours: { start: e.target.value } } })
              }
            />
          </Field>
          <Field label="Silence — fin">
            <Input
              defaultValue={settings.notifications.quiet_hours.end}
              onBlur={(e) => onSave({ notifications: { quiet_hours: { end: e.target.value } } })}
            />
          </Field>
        </div>

        <Button onClick={() => api.post("/push/test")}>Envoyer un test</Button>
      </div>
    </Card>
  );
}

function FewShots({
  settings,
  onSave,
}: {
  settings: Settings;
  onSave: (p: Record<string, unknown>) => Promise<void>;
}) {
  const [raw, setRaw] = useState(
    settings.few_shots.map((f) => `${f.context}\n---\n${f.reply}`).join("\n===\n"),
  );

  return (
    <Field
      label={`Few-shots — ${settings.few_shots.length} exemple(s)`}
      hint="Tes vraies replies passées. Format : tweet, ligne « --- », ta réponse. Séparer les exemples par une ligne « === ». C'est ce qui calibre le ton, bien plus que les consignes."
    >
      <Textarea
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        onBlur={() => onSave({ few_shots: parseFewShots(raw) })}
        placeholder={"le tweet auquel je répondais\n---\nma réponse\n===\nautre tweet\n---\nautre réponse"}
      />
    </Field>
  );
}

function parseFewShots(raw: string): { context: string; reply: string }[] {
  return raw
    .split("===")
    .map((block) => block.trim())
    .filter(Boolean)
    .map((block) => {
      const [context = "", reply = ""] = block.split("---").map((s) => s.trim());
      return { context, reply };
    })
    .filter((f) => f.context && f.reply);
}

function Assets({
  settings,
  onSave,
}: {
  settings: Settings;
  onSave: (p: Record<string, unknown>) => Promise<void>;
}) {
  const [raw, setRaw] = useState(settings.assets.map((a) => `${a.name} : ${a.hint}`).join("\n"));

  return (
    <Field
      label="Assets montrables"
      hint="Un par ligne, format « nom : description ». Ex. « Pet Town : GIF de la démo »."
    >
      <Textarea
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        onBlur={() =>
          onSave({
            assets: raw
              .split("\n")
              .map((line) => line.trim())
              .filter(Boolean)
              .map((line) => {
                const idx = line.indexOf(":");
                return idx === -1
                  ? { name: line, hint: line }
                  : { name: line.slice(0, idx).trim(), hint: line.slice(idx + 1).trim() };
              }),
          })
        }
      />
    </Field>
  );
}

function CoreList({ accounts, onChange }: { accounts: Account[]; onChange: () => void }) {
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const core = accounts.filter((a) => a.tier === "core");
  const discovery = accounts.filter((a) => a.tier === "discovery");

  async function add() {
    setBusy(true);
    setError(null);
    try {
      const handles = splitList(input.replace(/@/g, ""));
      const result = await api.post<{ unresolved: string[] }>("/settings/accounts", { handles });
      if (result.unresolved.length > 0) {
        setError(`Non résolus : ${result.unresolved.join(", ")}`);
      }
      setInput("");
      onChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Ajout impossible");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <h2 className="mb-1 text-sm font-medium">Core list — {core.length} comptes</h2>
      <p className="mb-3 text-[11px] text-[var(--color-muted)]">
        Résoudre un handle coûte un « user read » ($0.010). C'est fait une seule fois, ici.
      </p>

      <div className="flex gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="handle1, handle2, …"
        />
        <Button onClick={add} disabled={busy || !input.trim()} variant="primary">
          Ajouter
        </Button>
      </div>

      {error && <p className="mt-2 text-xs text-[var(--color-bad)]">{error}</p>}

      <ul className="mt-3 flex flex-col divide-y divide-[var(--color-line)]">
        {core.map((account) => (
          <li key={account.id} className="flex items-center justify-between py-2">
            <div className="min-w-0">
              <p className="truncate text-sm">@{account.handle}</p>
              <p className="text-[11px] text-[var(--color-muted)] tabular-nums">
                {account.swipes_right}↑ {account.swipes_left}↓ · poids{" "}
                {account.learned_weight.toFixed(2)}
                {!account.x_user_id && " · non résolu"}
              </p>
            </div>
            <Button
              variant="danger"
              onClick={async () => {
                await api.del(`/settings/accounts/${account.id}`);
                onChange();
              }}
            >
              Retirer
            </Button>
          </li>
        ))}
        {core.length === 0 && (
          <li className="py-3 text-xs text-[var(--color-muted)]">
            Core list vide — le Scout n'aura rien à lire.
          </li>
        )}
      </ul>

      {discovery.length > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-xs text-[var(--color-muted)]">
            {discovery.length} comptes découverte repérés
          </summary>
          <ul className="mt-2 flex flex-col divide-y divide-[var(--color-line)]">
            {discovery
              .filter((a) => a.swipes_right > a.swipes_left)
              .slice(0, 15)
              .map((account) => (
                <li key={account.id} className="flex items-center justify-between py-2">
                  <span className="text-sm">@{account.handle}</span>
                  <Button
                    onClick={async () => {
                      await api.patch(`/settings/accounts/${account.id}`, { tier: "core" });
                      onChange();
                    }}
                  >
                    Promouvoir
                  </Button>
                </li>
              ))}
          </ul>
        </details>
      )}
    </Card>
  );
}

interface JobRun {
  id: number;
  job: string;
  day: string;
  status: string;
  items: number;
  est_cost_usd: number;
  started_at: string;
  error: string | null;
}

function Jobs() {
  const [runs, setRuns] = useState<JobRun[]>([]);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(() => {
    api.get<{ runs: JobRun[] }>("/stats/jobs").then((r) => setRuns(r.runs.slice(0, 8)));
  }, []);

  useEffect(load, [load]);

  async function trigger(path: string, label: string) {
    await api.post(path);
    setMessage(`${label} lancé — le résultat apparaît ci-dessous d'ici une minute.`);
    setTimeout(load, 4000);
  }

  return (
    <Card>
      <h2 className="mb-3 text-sm font-medium">Jobs</h2>
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => trigger("/jobs/scout?batch=evening", "Scout")}>Lancer un pull</Button>
        <Button onClick={() => trigger("/jobs/tracker", "Tracker")}>Snapshot stats</Button>
        <Button onClick={() => trigger("/jobs/orbit", "Orbit")}>Cibler des pairs</Button>
        <Button onClick={() => trigger("/jobs/rescore", "Re-scoring")}>Re-scorer le deck</Button>
        <Button onClick={() => trigger("/jobs/expire", "Expiration")}>Expirer les cartes</Button>
      </div>

      {message && <p className="mt-2 text-xs text-[var(--color-muted)]">{message}</p>}

      <p className="mt-2 text-[11px] text-[var(--color-muted)]">
        « Re-scorer le deck » reclasse les cartes en attente avec Haiku sans relire quoi que ce
        soit chez X — utile si la classification a échoué pendant un pull, ou après avoir modifié
        tes territoires.
      </p>

      <ul className="mt-3 flex flex-col divide-y divide-[var(--color-line)] text-xs">
        {runs.map((run) => (
          <li key={run.id} className="flex items-center justify-between py-2">
            <span>
              {run.job} · {new Date(run.started_at).toLocaleTimeString("fr-FR", { timeStyle: "short" })}
            </span>
            <span className="flex items-center gap-2 tabular-nums">
              <span className="text-[var(--color-muted)]">
                {run.items} · {Number(run.est_cost_usd).toFixed(3)} $
              </span>
              <Badge
                tone={run.status === "ok" ? "good" : run.status === "error" ? "bad" : "warn"}
              >
                {run.status}
              </Badge>
            </span>
          </li>
        ))}
        {runs.length === 0 && <li className="py-2 text-[var(--color-muted)]">Aucun job encore.</li>}
      </ul>
    </Card>
  );
}
